import policy from './pricing-policy-2025.json';

// Returns the first value in sortedArr that is >= target, or null if none exists.
function snapUp(target, sortedArr) {
  return sortedArr.find((v) => v >= target) ?? null;
}

// Check if the project location falls in a seismic special area.
function isSeismicSpecialArea(locationState, locationCityCounty) {
  if (!locationState || !locationCityCounty) return false;
  const cities = policy.seismic_special_areas[locationState];
  if (!cities) return false;
  const normalized = locationCityCounty.toLowerCase().trim();
  return cities.some((c) => {
    const cn = c.toLowerCase();
    return normalized.includes(cn) || cn.includes(normalized);
  });
}

/**
 * Calculate A&A Engineering quote for drawings and (optionally) calculations.
 *
 * @param {{
 *   buildingType: string,
 *   widthFt: number,
 *   lengthFt: number,
 *   wantsCalculations: boolean,
 *   isRush: boolean,
 *   isPriority: boolean,
 *   isResidential: boolean,
 *   isLoafingShed: boolean,
 *   locationState: string,
 *   locationCityCounty: string,
 * }} input
 * @returns {{
 *   baseDrawings: number,
 *   baseCalcs: number,
 *   loafingUplift: number,
 *   drawingsTotal: number,
 *   calculationsTotal: number,
 *   total: number,
 *   appliedModifiers: string[],
 *   forcedCalculations: boolean,
 *   snappedWidth: number|null,
 *   snappedLength: number|null,
 *   dimensionWarning: boolean,
 *   error: string|null,
 * }}
 */
export function calculateEngineeringQuote(input) {
  const {
    buildingType,
    widthFt,
    lengthFt,
    wantsCalculations = false,
    isRush = false,
    isPriority = false,
    isResidential = false,
    isLoafingShed = false,
    locationState = '',
    locationCityCounty = '',
  } = input;

  const empty = {
    baseDrawings: 0, baseCalcs: 0, loafingUplift: 0,
    drawingsTotal: 0, calculationsTotal: 0, total: 0,
    appliedModifiers: [], forcedCalculations: false,
    snappedWidth: null, snappedLength: null, dimensionWarning: false,
    error: null,
  };

  if (!buildingType) return empty;

  const bt = policy.building_types.find((b) => b.code === buildingType);
  if (!bt) return { ...empty, error: `Unknown building type: ${buildingType}` };

  const matrix = policy.base_price_matrices[bt.matrix_key];
  if (!matrix) return { ...empty, error: `Missing matrix for ${bt.matrix_key}` };

  const snappedWidth = snapUp(Number(widthFt), matrix.widths_ft);
  const snappedLength = snapUp(Number(lengthFt), matrix.lengths_ft);

  if (snappedWidth === null)
    return { ...empty, error: `Width ${widthFt}ft exceeds the maximum supported by this building type (${matrix.widths_ft[matrix.widths_ft.length - 1]}ft).` };
  if (snappedLength === null)
    return { ...empty, error: `Length ${lengthFt}ft exceeds the maximum supported (200ft).` };

  const widthIdx = matrix.widths_ft.indexOf(snappedWidth);
  const lengthIdx = matrix.lengths_ft.indexOf(snappedLength);

  const baseDrawings = matrix.drawings[lengthIdx][widthIdx];
  const baseCalcs = matrix.calculations[lengthIdx][widthIdx];

  // Loafing shed uplift ($250 to drawings, applies before modifiers)
  const loafingUplift = isLoafingShed && bt.is_loafing_shed_eligible ? 250 : 0;

  // Seismic auto-detect
  const seismic = isSeismicSpecialArea(locationState, locationCityCounty);
  const forcedCalculations = seismic;
  const appliedModifiers = [];

  // Build modifier totals
  let drawingsPct = 0;
  let calcsPct = 0;
  let residentialMultiplier = 1;

  if (isResidential) {
    residentialMultiplier = 1.5;
    appliedModifiers.push('residential');
  }
  if (isRush) {
    drawingsPct += 50;
    appliedModifiers.push('rush');
  }
  if (isPriority) {
    drawingsPct += 25;
    appliedModifiers.push('priority');
  }
  if (seismic) {
    drawingsPct += 25;
    calcsPct += 40;
    appliedModifiers.push('seismic_special_area');
  }

  const drawingsTotal = Math.round(
    (baseDrawings + loafingUplift) * (1 + drawingsPct / 100) * residentialMultiplier * 100
  ) / 100;

  const effectiveWantsCalcs = wantsCalculations || forcedCalculations;
  const calculationsTotal = effectiveWantsCalcs
    ? Math.round(baseCalcs * (1 + calcsPct / 100) * residentialMultiplier * 100) / 100
    : 0;

  return {
    baseDrawings,
    baseCalcs,
    loafingUplift,
    drawingsTotal,
    calculationsTotal,
    total: drawingsTotal + calculationsTotal,
    appliedModifiers,
    forcedCalculations,
    snappedWidth,
    snappedLength,
    dimensionWarning:
      snappedWidth !== Number(widthFt) || snappedLength !== Number(lengthFt),
    error: null,
  };
}

export const AA_BUILDING_TYPES = policy.building_types.map((bt) => ({
  value: bt.code,
  label: bt.label,
  isLoafingShedEligible: bt.is_loafing_shed_eligible,
}));
